import { useEffect, useRef, useCallback, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { Check, Loader2 } from 'lucide-react';
import { saveCalendarNote, updateCalendarNoteContent } from '../../hooks/useCalendarNotes';
import type { CalendarNote } from '../../types/calendar';

interface MeetingEditorProps {
  meetingKey: string;
  meetingTitle: string;
  meetingDate: string;
  existingNote: CalendarNote | null;
}

export function MeetingEditor({ meetingKey, meetingTitle, meetingDate, existingNote }: MeetingEditorProps) {
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const noteIdRef = useRef<string | null>(existingNote?.id ?? null);

  useEffect(() => {
    noteIdRef.current = existingNote?.id ?? null;
  }, [existingNote?.id]);

  const performSave = useCallback(async (html: string) => {
    setSaveStatus('saving');
    try {
      if (noteIdRef.current) {
        await updateCalendarNoteContent(noteIdRef.current, html);
      } else {
        const result = await saveCalendarNote({
          meetingKey,
          title: meetingTitle,
          content: html,
          date: meetingDate,
        });
        noteIdRef.current = result.id;
      }
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch {
      setSaveStatus('idle');
    }
  }, [meetingKey, meetingTitle, meetingDate]);

  const debouncedSave = useCallback((html: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => performSave(html), 1500);
  }, [performSave]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({ placeholder: 'Start typing your meeting notes...' }),
    ],
    content: existingNote?.content ?? '',
    onUpdate: ({ editor }) => {
      debouncedSave(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'tiptap-content',
      },
    },
  });

  useEffect(() => {
    if (editor && existingNote?.content && !editor.isFocused && editor.isEmpty) {
      editor.commands.setContent(existingNote.content);
    }
  }, [editor, existingNote?.content]);

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex items-center justify-end px-4 py-2">
        {saveStatus === 'saving' && (
          <span className="flex items-center gap-1.5 text-micro text-accent animate-pulse">
            <Loader2 size={12} className="animate-spin" />
            Saving...
          </span>
        )}
        {saveStatus === 'saved' && (
          <span className="flex items-center gap-1.5 text-micro text-success">
            <Check size={12} />
            Saved
          </span>
        )}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-8">
        <EditorContent editor={editor} className="tiptap-editor" />
      </div>
    </div>
  );
}
